import { AlertTriangle, TrendingUp, Eye, CheckCircle, Star, Minus, ChevronDown, XCircle } from 'lucide-react';
import { type ViewMode } from '@/data/regulatoryData';

interface LegendItem {
  label: string;
  range: string;
  color: string;
  icon: React.ElementType;
  description: string;
}

// Pressure legend items (higher score = more pressure = bad)
// Aligned with backend thresholds (scoring_engine.py STATUS_THRESHOLDS)
const pressureLegendItems: LegendItem[] = [
  {
    label: 'Stable',
    range: '0-34',
    color: 'bg-pressure-stable',
    icon: CheckCircle,
    description: 'Low regulatory pressure'
  },
  {
    label: 'Watch',
    range: '35-54',
    color: 'bg-pressure-watch',
    icon: Eye,
    description: 'Increasing activity'
  },
  {
    label: 'Tightening',
    range: '55-74',
    color: 'bg-pressure-tightening',
    icon: TrendingUp,
    description: 'Active enforcement'
  },
  {
    label: 'High Pressure',
    range: '75-100',
    color: 'bg-pressure-high',
    icon: AlertTriangle,
    description: 'Major action underway'
  },
];

// Opportunity legend items (higher score = more opportunity = good)
// Colors are inverted from pressure - green is good, red is bad
// Aligned with backend thresholds
const opportunityLegendItems: LegendItem[] = [
  {
    label: 'Low',
    range: '0-34',
    color: 'bg-pressure-high', // Red = low opportunity
    icon: XCircle,
    description: 'Limited opportunity'
  },
  {
    label: 'Moderate',
    range: '35-54',
    color: 'bg-pressure-tightening', // Dark orange
    icon: Minus,
    description: 'Some barriers'
  },
  {
    label: 'Good',
    range: '55-74',
    color: 'bg-pressure-watch', // Orange/yellow
    icon: ChevronDown,
    description: 'Favorable conditions'
  },
  {
    label: 'Excellent',
    range: '75-100',
    color: 'bg-pressure-stable', // Green = excellent opportunity
    icon: Star,
    description: 'Optimal market'
  },
];

interface LegendProps {
  viewMode?: ViewMode;
}

const Legend = ({ viewMode = 'pressure' }: LegendProps) => {
  const isPressure = viewMode === 'pressure';
  const legendItems = isPressure ? pressureLegendItems : opportunityLegendItems;
  const title = isPressure ? 'Pressure Level' : 'Opportunity Level';

  return (
    <div className="fixed bottom-6 left-6 z-[1000]">
      <div className="glass rounded-lg shadow-glass p-3">
        <p className="text-text-tertiary text-[10px] uppercase tracking-wider mb-2.5 font-medium">
          {title}
        </p>
        <div className="space-y-2">
          {legendItems.map((item) => {
            const Icon = item.icon;
            return (
              <div key={item.label} className="flex items-center gap-2.5 group">
                <div className={`w-3.5 h-3.5 rounded ${item.color} flex items-center justify-center`}>
                  <Icon className="w-2 h-2 text-white/80" />
                </div>
                <div className="flex flex-col">
                  <span className="text-text-primary text-xs font-medium leading-tight">
                    {item.label}
                  </span>
                  <span className="text-text-tertiary text-[10px] leading-tight">
                    {item.range}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Keyboard hint */}
        <div className="mt-3 pt-2.5 border-t border-white/5">
          <p className="text-text-tertiary text-[10px]">
            Press <kbd className="px-1 py-0.5 bg-white/5 rounded text-text-secondary">?</kbd> for shortcuts
          </p>
        </div>
      </div>
    </div>
  );
};

export default Legend;
